import React from 'react';
import CountrySplitChart from './components/CountrySplitChart';
import MsmeSplitChart from './components/MsmeSplitChart';
import CategorySplitChart from './components/CategorySplitChart';
import CurrencySplitChart from './components/CurrencySplitChart';
import OnboardingTrendsChart from './components/OnboardingTrendsChart';
import RegionSplitChart from './components/RegionSplitChart';
import PaymentTermsSplitChart from './components/PaymentTermsSplitChart';
import SupplierGroupSplitChart from './components/SupplierGroupSplitChart';
import CommodityCodeSplitChart from './components/CommodityCodeSplitChart';
import DeliveryTermsSplitChart from './components/DeliveryTermsSplitChart';
import TurnaroundByStageChart from './components/TurnaroundByStageChart';
import { dashboardService } from '../../services/apiService';
import { useApi } from '../../hooks/useApi';
import { useToast } from '../../components/ui/Toast';
import { GlassCard, KpiCard, Icon, useCampaignFonts } from '../campaigning-tool/campaign-os';
import '../campaigning-tool/campaign-os/campaign-os.css';

/**
 * Dashboard Overview — migrated to the CampaignOS "Light Enterprise Glassmorphism"
 * theme. VISUAL ONLY: every KPI, data fetch, chart, and value is byte-for-byte the
 * same as before — only the presentation changed. The `.campaign-os` wrapper supplies
 * the ivory canvas + Hanken font + frosted-glass surfaces; `.cos-dashboard` re-points
 * the shared charts' `--dash-*` CSS variables at the CampaignOS palette (page-scoped),
 * so the charts recolor WITHOUT touching any shared component. KPI cards use the new
 * KpiCard; panels use GlassCard.
 */

// KPI presentation only (icon + tone). Values/titles/subtitles are unchanged.
const KPI_STYLE = {
  primary: { icon: 'groups', tone: 'teal' },
  warning: { icon: 'pending_actions', tone: 'beige' },
  destructive: { icon: 'cancel', tone: 'neutral' },
  success: { icon: 'trending_up', tone: 'teal' },
};

const DashboardOverview = () => {
  const { error: toastError } = useToast();
  useCampaignFonts();

  const { data: metrics, loading, error, refetch } = useApi(
    () => dashboardService.getMetrics(),
    {
      onError: (err) => {
        console.error('Dashboard metrics error:', err);
        toastError('Failed to load dashboard metrics. Using cached data.');
      },
    }
  );

  const monthName = new Date().toLocaleString('en-US', { month: 'long' });

  const metricsData = metrics?.data ? [
    { title: 'Total Vendors', value: String(metrics.data.totalVendors?.value || 0), subtitle: 'onboarded to date', color: 'primary' },
    { title: 'Pending Approvals', value: String(metrics.data.pendingApprovals?.value || 0), subtitle: 'awaiting action', color: 'warning' },
    { title: 'Rejected Vendors', value: String(metrics.data.rejectedVendors?.value || 0), subtitle: 'requests declined', color: 'destructive' },
    { title: `${monthName} Onboarded`, value: String(metrics.data.thisMonthOnboarded?.value || 0), subtitle: 'current month', color: 'success' },
  ] : [];

  return (
    <div className="campaign-os cos-dashboard flex h-full min-w-0 flex-col gap-4 pb-3">
      {/* Error Message with Retry */}
      {error && (
        <GlassCard>
          <div className="flex items-center justify-between gap-3">
            <div>
              <p className="text-sm font-semibold text-cos-status-coral">Error Loading Dashboard</p>
              <p className="mt-0.5 text-[11px] text-cos-ink-muted">{error}</p>
            </div>
            <button
              onClick={refetch}
              className="rounded-xl border border-cos-line bg-white/60 px-3 py-1.5 text-[11px] font-semibold text-cos-ink-secondary transition-colors hover:border-cos-teal hover:text-cos-teal-deep"
            >
              Retry
            </button>
          </div>
        </GlassCard>
      )}

      {/* Row 1: Key Metrics Cards */}
      <div className="grid flex-shrink-0 grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {loading
          ? Array.from({ length: 4 }).map((_, index) => (
              <div key={index} className="glass-card p-4">
                <div className="h-14 animate-pulse rounded-xl bg-white/40" />
              </div>
            ))
          : metricsData.map((metric, index) => {
              const s = KPI_STYLE[metric.color] || KPI_STYLE.primary;
              return (
                <KpiCard
                  key={index}
                  label={metric.title}
                  value={metric.value}
                  caption={metric.subtitle}
                  icon={s.icon}
                  tone={s.tone}
                />
              );
            })}
      </div>

      {/* Row 2: MSME | MSME Category | Currency */}
      <GlassCard>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          <div className="flex flex-col border-b border-cos-line pb-3 md:border-b-0 md:border-r md:pb-0 md:pr-4">
            <MsmeSplitChart />
          </div>
          <div className="flex flex-col border-b border-cos-line pb-3 md:border-b-0 md:pb-0 lg:border-r lg:pr-4">
            <CategorySplitChart />
          </div>
          <div className="flex flex-col">
            <CurrencySplitChart />
          </div>
        </div>
      </GlassCard>

      {/* Row 3: Onboarding Trends (full width) */}
      <GlassCard>
        <OnboardingTrendsChart />
      </GlassCard>

      {/* Row 4: Region | Country */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <GlassCard><RegionSplitChart /></GlassCard>
        <GlassCard><CountrySplitChart /></GlassCard>
      </div>

      {/* Row 5: Payment Terms | Supplier Group */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <GlassCard><PaymentTermsSplitChart /></GlassCard>
        <GlassCard><SupplierGroupSplitChart /></GlassCard>
      </div>

      {/* Row 6: Commodity Code | Delivery Terms */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <GlassCard><CommodityCodeSplitChart /></GlassCard>
        <GlassCard><DeliveryTermsSplitChart /></GlassCard>
      </div>

      {/* Row 7: Turnaround by Stage (full width) */}
      <GlassCard><TurnaroundByStageChart /></GlassCard>
    </div>
  );
};

export default DashboardOverview;
